import { useMemo } from 'react';
import { isEmpty, noop } from 'lodash';

import { PatientGroup, PatientGroupQuery } from '@dreryk/egabinet-patient-frontend-api';

import {
   createDynamicColumnsWidth,
   EditIcon,
   HexColorDot,
   HexColorDotSize,
   Link,
   TableColumnConfiguration,
   Text,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';
import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { Slot } from '@dreryk/watermelon';
import { useTranslate } from '@dreryk/react-common';

import { protectFeature } from '~libs.shared.system';

import { translationKeys } from '../../translations';

import { formatPatientGroupEffects } from './PredefinedPatientGroupsTable.utils';
import { PredefinedPatientGroupsUpdateModalOpener } from './update/PredefinedPatientGroupsUpdateModal.opener';

export const usePredefinedPatientGroupsTableDataConfiguration = (): LazyLoadedDataConfigurationByCqrsQuery<PatientGroup> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<PatientGroup>(() => {
      const query = new PatientGroupQuery();
      query.predefined = Slot.of(true);
      return query;
   });
};

// eslint-disable-next-line max-lines-per-function
export const usePredefinedPatientGroupsTableColumnsConfiguration = (): TableColumnConfiguration<PatientGroup>[] => {
   const translate = useTranslate();
   const managingPatientPortalAvailable = protectFeature('managingPatientPortal');

   // eslint-disable-next-line max-lines-per-function
   return useMemo<TableColumnConfiguration<PatientGroup>[]>(() => {
      const columnsWidth = createDynamicColumnsWidth({
         color: '64px',
         name: '240px',
         effect: '100%',
         options: '56px',
      });

      return [
         {
            label: translationKeys.color,
            name: 'color',
            width: columnsWidth.color,
            sortingDisabled: true,
            accessor: 'primaryColor',
            renderer: ({ bean: { primaryColor, darkModeColor, id } }) => (
               <HexColorDot
                  testId={id}
                  color={primaryColor.asHexString()}
                  darkModeColor={darkModeColor?.asHexString()}
                  size={HexColorDotSize.SMALL}
               />
            ),
         },
         {
            label: translationKeys.name,
            name: 'name',
            width: columnsWidth.name,
            sortingDisabled: true,
            accessor: 'name',
         },
         ...(managingPatientPortalAvailable
            ? [
                 {
                    label: translationKeys.effect,
                    name: 'effect',
                    width: columnsWidth.effect,
                    sortingDisabled: true,
                    placeholder: TextPlaceholder.NO_DATA,
                    accessor: noop,
                    renderer: ({ bean: { possibleEffects, effects } }: ValueRendererProps<PatientGroup>) => {
                       if (isEmpty(possibleEffects)) {
                          return <Text testId={'predefinedPatientGroupsNoEffects'}>{''}</Text>;
                       }

                       if (isEmpty(effects)) {
                          return null;
                       }

                       return <>{formatPatientGroupEffects(effects, translate)}</>;
                    },
                 },
                 {
                    name: 'options',
                    width: columnsWidth.options,
                    sortingDisabled: true,
                    accessor: noop,
                    renderer: ({ bean }: ValueRendererProps<PatientGroup>) => {
                       if (isEmpty(bean.possibleEffects)) {
                          return null;
                       }

                       return (
                          <PredefinedPatientGroupsUpdateModalOpener patientGroup={bean}>
                             {({ openModal }) => (
                                <Link testId={'predefinedPatientGroupsEdit'} icon={<EditIcon />} onClick={openModal} />
                             )}
                          </PredefinedPatientGroupsUpdateModalOpener>
                       );
                    },
                 },
              ]
            : []),
      ];
   }, [managingPatientPortalAvailable, translate]);
};
